import React from 'react';
import {
  CollapsibleSection,
  DatePicker,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import type {
  DiscoveryLocation,
  InfectionSite,
  InfectionStatus,
  InfectionType,
} from '../../services/api';
import type { InfectionSourceEvidence } from '../../services/api/types.gen';
import {
  COVID_SYMPTOMS,
  DISCOVERY_LOCATIONS,
  INFECTION_SITES,
  INFECTION_TYPE_OPTIONS,
} from './infectionConstants';
import type {
  InfectionLogFormState,
  UpdateInfectionLogField,
} from './infectionLogForm';
import { InfectionFieldLabel } from './infectionLogFormFields';

interface InfectionInformationSectionProps {
  form: InfectionLogFormState;
  updateField: UpdateInfectionLogField;
  isEditing: boolean;
  today: Date;
  sourceEvidence: InfectionSourceEvidence | null;
}

const STATUS_OPTIONS: InfectionStatus[] = [
  'investigating',
  'probable',
  'confirmed',
];

const InfectionInformationSection: React.FC<
  InfectionInformationSectionProps
> = ({ form, updateField, isEditing, today, sourceEvidence }) => {
  const isCovid = form.infectionType === 'covid_19';

  return (
    <CollapsibleSection title='Infection Information' defaultOpen>
      <div className='space-y-3'>
        {/* Status */}
        <div>
          <InfectionFieldLabel>Status</InfectionFieldLabel>
          <div className='flex gap-4'>
            {[
              ...(form.infectionStatus === 'unknown' ||
              form.infectionStatus === 'resolved'
                ? [form.infectionStatus]
                : []),
              ...STATUS_OPTIONS,
            ].map(s => (
              <label
                key={s}
                className='flex items-center gap-1.5 cursor-pointer text-sm'
              >
                <input
                  type='radio'
                  name='infection_status'
                  value={s}
                  checked={form.infectionStatus === s}
                  onChange={() => updateField('infectionStatus', s)}
                  className='accent-primary'
                />
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </label>
            ))}
          </div>
        </div>

        {/* Date of Onset */}
        {!form.presentUponAdmission && (
          <div>
            <InfectionFieldLabel>Date of Onset</InfectionFieldLabel>
            <DatePicker
              date={form.dateOfOnset}
              onDateChange={d => updateField('dateOfOnset', d)}
              maxDate={today}
            />
          </div>
        )}
        <fieldset>
          <legend className='text-sm font-medium mb-1'>
            Present Upon Admission
          </legend>
          <div className='flex gap-4'>
            {[
              { label: 'Yes', value: true },
              { label: 'No', value: false },
              { label: 'Unknown', value: null },
            ].map(option => (
              <label
                key={option.label}
                className='flex items-center gap-1.5 text-sm cursor-pointer'
              >
                <input
                  type='radio'
                  name='present_upon_admission'
                  checked={form.presentUponAdmission === option.value}
                  onChange={() => {
                    updateField('presentUponAdmission', option.value);
                    if (option.value === true) {
                      updateField('dateOfOnset', undefined);
                    }
                  }}
                  className='accent-primary'
                />
                {option.label}
              </label>
            ))}
          </div>
        </fieldset>

        {/* Infection Type */}
        <div>
          <InfectionFieldLabel required>Infection Type</InfectionFieldLabel>
          <Select
            value={form.infectionType}
            onValueChange={v =>
              updateField('infectionType', v as InfectionType)
            }
            disabled={isEditing && !form.canResolveType}
          >
            <SelectTrigger aria-label='Infection Type'>
              <SelectValue placeholder='Select' />
            </SelectTrigger>
            <SelectContent>
              {form.infectionType === 'unknown' && (
                <SelectItem value='unknown'>
                  {sourceEvidence?.type_description ?? 'Type unavailable'}
                </SelectItem>
              )}
              {INFECTION_TYPE_OPTIONS.map(t => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {form.infectionType === 'other' && (
            <input
              type='text'
              maxLength={25}
              placeholder='Type description'
              aria-label='Other infection type'
              disabled={isEditing && !form.canResolveType}
              value={form.infectionTypeOther}
              onChange={e => updateField('infectionTypeOther', e.target.value)}
              className='mt-1.5 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm placeholder:text-placeholder focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'
            />
          )}
        </div>

        {/* Infection Site */}
        <div>
          <InfectionFieldLabel>Infection Site</InfectionFieldLabel>
          <Select
            value={form.infectionSite}
            onValueChange={v =>
              updateField('infectionSite', v as InfectionSite)
            }
          >
            <SelectTrigger>
              <SelectValue placeholder='Select' />
            </SelectTrigger>
            <SelectContent>
              {INFECTION_SITES.map(s => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {form.infectionSite === 'other' && (
            <input
              type='text'
              maxLength={25}
              placeholder='Site description'
              value={form.infectionSiteOther}
              onChange={e => updateField('infectionSiteOther', e.target.value)}
              className='mt-1.5 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm placeholder:text-placeholder focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'
            />
          )}
        </div>

        {/* Patient Location When Discovered */}
        <div>
          <InfectionFieldLabel>
            Patient Location When Discovered
          </InfectionFieldLabel>
          <Select
            value={form.discoveryLocation}
            onValueChange={v =>
              updateField('discoveryLocation', v as DiscoveryLocation)
            }
          >
            <SelectTrigger>
              <SelectValue placeholder='Select' />
            </SelectTrigger>
            <SelectContent>
              {DISCOVERY_LOCATIONS.map(l => (
                <SelectItem key={l.value} value={l.value}>
                  {l.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {form.discoveryLocation === 'other' && (
            <input
              type='text'
              maxLength={25}
              placeholder='Location description'
              value={form.discoveryLocationOther}
              onChange={e =>
                updateField('discoveryLocationOther', e.target.value)
              }
              className='mt-1.5 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm placeholder:text-placeholder focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'
            />
          )}
        </div>

        {/* Communicable */}
        <div>
          <InfectionFieldLabel>Communicable?</InfectionFieldLabel>
          <div className='flex gap-4'>
            {['yes', 'no'].map(v => (
              <label
                key={v}
                className='flex items-center gap-1.5 cursor-pointer text-sm'
              >
                <input
                  type='radio'
                  name='communicable'
                  value={v}
                  checked={form.isCommunicable === v}
                  onChange={() => updateField('isCommunicable', v)}
                  className='accent-primary'
                />
                {v.charAt(0).toUpperCase() + v.slice(1)}
              </label>
            ))}
          </div>
        </div>

        {/* Description / Signs & Symptoms OR COVID-19 Screening */}
        {!isCovid ? (
          <>
            <div>
              <InfectionFieldLabel>Infection Description</InfectionFieldLabel>
              <textarea
                maxLength={2000}
                rows={4}
                value={form.infectionDescription}
                onChange={e =>
                  updateField('infectionDescription', e.target.value)
                }
                className='flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-placeholder focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none'
              />
            </div>
            <div>
              <InfectionFieldLabel>Signs & Symptoms</InfectionFieldLabel>
              <textarea
                maxLength={2000}
                rows={4}
                value={form.signsSymptomsText}
                onChange={e => updateField('signsSymptomsText', e.target.value)}
                className='flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-placeholder focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none'
              />
            </div>
          </>
        ) : (
          <div className='border rounded-md p-3 space-y-2 bg-muted/20'>
            <p className='text-sm font-medium'>
              Patient Symptom Screening for COVID-19
            </p>
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-2'>
              {COVID_SYMPTOMS.map(symptom => (
                <div
                  key={symptom.key}
                  className='flex items-center justify-between text-sm'
                >
                  <span>{symptom.label}</span>
                  <div className='flex gap-3'>
                    {['yes', 'no'].map(v => (
                      <label
                        key={v}
                        className='flex items-center gap-1 cursor-pointer'
                      >
                        <input
                          type='radio'
                          name={`covid_${symptom.key}`}
                          value={v}
                          checked={
                            form.covidSymptoms[symptom.key] === (v === 'yes')
                          }
                          onChange={() =>
                            updateField('covidSymptoms', prev => ({
                              ...prev,
                              [symptom.key]: v === 'yes',
                            }))
                          }
                          className='accent-primary'
                        />
                        {v.charAt(0).toUpperCase() + v.slice(1)}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div>
              <InfectionFieldLabel>
                Contact with confirmed/suspected COVID-19 case?
              </InfectionFieldLabel>
              <div className='flex gap-4'>
                {['yes', 'no', 'uncertain'].map(v => (
                  <label
                    key={v}
                    className='flex items-center gap-1.5 cursor-pointer text-sm'
                  >
                    <input
                      type='radio'
                      name='covid_contact'
                      value={v}
                      checked={form.covidContact === v}
                      onChange={() => updateField('covidContact', v)}
                      className='accent-primary'
                    />
                    {v.charAt(0).toUpperCase() + v.slice(1)}
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
};

export default InfectionInformationSection;
